import { useEffect, useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { FileText, Clock, CheckCircle, XCircle, Download } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import axios from "axios";
import { toast } from "sonner";
import { generateQuotePDF } from "@/utils/pdfGenerator";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const statusConfig = {
  pending: { label: "Pending Review", icon: Clock, color: "text-yellow-600" },
  reviewed: { label: "Reviewed", icon: CheckCircle, color: "text-blue-600" },
  quoted: { label: "Quoted", icon: CheckCircle, color: "text-green-600" },
  closed: { label: "Closed", icon: XCircle, color: "text-gray-600" },
};

export default function MyQuotes() {
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchQuotes();
  }, []);

  const fetchQuotes = async () => {
    try {
      const response = await axios.get(`${API}/quotes/my`);
      setQuotes(response.data);
    } catch (error) {
      console.error("Error fetching quotes:", error);
      toast.error("Failed to load quotes");
    } finally {
      setLoading(false);
    }
  };

  const handleExportPDF = (quote) => {
    try {
      const fileName = generateQuotePDF(quote);
      toast.success(`Quote exported as ${fileName}`);
    } catch (error) {
      console.error("Error generating PDF:", error);
      toast.error("Failed to export quote");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col">
        <Navbar />
        <div className="flex-1 flex items-center justify-center">
          <div className="text-xl">Loading quotes...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />

      <main className="flex-1">
        <section className="bg-muted/20 border-b">
          <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 py-8">
            <h1 className="text-4xl font-bold tracking-tight" style={{ fontFamily: 'Manrope' }} data-testid="my-quotes-heading">
              My Quote Requests
            </h1>
            <p className="text-lg text-muted-foreground mt-2">Track your submitted quote requests and their status</p>
          </div>
        </section>

        <section className="py-12">
          <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
            {quotes.length === 0 ? (
              <Card className="p-12 text-center">
                <FileText className="h-16 w-16 text-muted-foreground mx-auto mb-4" />
                <h3 className="text-xl font-semibold mb-2">No Quote Requests</h3>
                <p className="text-muted-foreground">You haven't submitted any quote requests yet.</p>
              </Card>
            ) : (
              <div className="space-y-6" data-testid="quotes-list">
                {quotes.map((quote) => {
                  const StatusIcon = statusConfig[quote.status]?.icon || Clock;
                  const statusColor = statusConfig[quote.status]?.color || "text-gray-600";
                  const statusLabel = statusConfig[quote.status]?.label || quote.status;

                  return (
                    <Card key={quote.id} className="p-6" data-testid={`quote-card-${quote.id}`}>
                      <div className="flex justify-between items-start mb-4">
                        <div>
                          <div className="text-sm text-muted-foreground mb-1">
                            Quote ID: <span className="font-mono">{quote.id.substring(0, 8)}</span>
                          </div>
                          <div className="text-sm text-muted-foreground">
                            Submitted: {new Date(quote.created_at).toLocaleDateString('en-IN', {
                              year: 'numeric',
                              month: 'long',
                              day: 'numeric'
                            })}
                          </div>
                        </div>
                        <div className="flex items-center gap-4">
                          <div className={`flex items-center gap-2 ${statusColor}`}>
                            <StatusIcon className="h-5 w-5" />
                            <span className="font-semibold">{statusLabel}</span>
                          </div>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleExportPDF(quote)}
                            className="gap-2"
                            data-testid={`export-pdf-btn-${quote.id}`}
                          >
                            <Download className="h-4 w-4" />
                            Export PDF
                          </Button>
                        </div>
                      </div>

                      <div className="border-t border-border/50 pt-4">
                        <h3 className="text-lg font-semibold mb-3" style={{ fontFamily: 'Manrope' }}>Products</h3>
                        <ul className="space-y-2">
                          {quote.products.map((product, idx) => (
                            <li key={idx} className="flex justify-between items-center p-3 bg-muted/30 rounded-sm">
                              <div>
                                <span className="font-medium">{product.product_name}</span>
                                {product.notes && (
                                  <span className="text-sm text-muted-foreground ml-2">({product.notes})</span>
                                )}
                              </div>
                              <span className="text-sm font-semibold">{product.quantity} units</span>
                            </li>
                          ))}
                        </ul>

                        {quote.additional_notes && (
                          <div className="mt-4 p-4 bg-muted/30 rounded-sm">
                            <div className="text-sm font-semibold mb-1">Additional Notes:</div>
                            <p className="text-sm text-muted-foreground">{quote.additional_notes}</p>
                          </div>
                        )}
                      </div>
                    </Card>
                  );
                })}
              </div>
            )}
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}